// ============================================================
// CY_BORG Combat Arena — pure math + resolution helpers
// Rules follow the CY_BORG core; a HOUSE mode softens the edges.
// ============================================================

import { BACKLASH_TABLE, INFESTATION_TABLE } from "./character";

// --- die helpers ---
const d20 = () => Math.floor(Math.random() * 20) + 1;
const parseDie = (str) => {
  // "d6", "d8+2", "2d6", "d10a" (autofire)
  const m = String(str || "").trim().match(/(\d*)d(\d+)([+-]\d+)?(a)?/i);
  if (!m) return null;
  return {
    count: m[1] ? parseInt(m[1], 10) : 1,
    sides: parseInt(m[2], 10),
    mod: m[3] ? parseInt(m[3], 10) : 0,
    autofire: !!m[4],
  };
};
const rollDie = (spec) => {
  if (!spec) return { rolls: [], total: 0, max: 0 };
  const rolls = [];
  for (let i = 0; i < spec.count; i++) rolls.push(Math.floor(Math.random() * spec.sides) + 1);
  const sum = rolls.reduce((a, b) => a + b, 0);
  const max = spec.count * spec.sides + spec.mod;
  return { rolls, total: sum + spec.mod, max };
};

// Small helper — is this weapon damage notation autofire (has "a" suffix)?
export function isAutofire(damage) {
  return /d\d+a\b/i.test(String(damage || ""));
}

// --- WEAPON MISHAP TABLE (physical fumble) — d6 ---
export const WEAPON_MISHAP_TABLE = [
  { roll: 1, effect: "WEAPON JAMS — unusable until you spend a round to clear it." },
  { roll: 2, effect: "DROPPED — weapon skids 3m in a random direction." },
  { roll: 3, effect: "SELF-STRIKE — take d2 damage from your own weapon." },
  { roll: 4, effect: "OVER-EXTEND — enemy gets ADV on their next attack against you." },
  { roll: 5, effect: "AMMO SPILLS — lose d3 rounds/charges. Reload before next attack." },
  { roll: 6, effect: "WEAPON BREAKS — snapped/fried. Needs repair or replacement." },
];
export function rollWeaponMishap() {
  const r = Math.floor(Math.random() * 6) + 1;
  return WEAPON_MISHAP_TABLE[r - 1];
}
export function rollFumbleFor(attackType) {
  // attackType: 'nano' | 'cyber' | 'physical'
  if (attackType === "nano") {
    const r = Math.floor(Math.random() * 20) + 1;
    return { table: "BACKLASH", die: "d20", roll: r, effect: BACKLASH_TABLE[r - 1].effect };
  }
  if (attackType === "cyber") {
    const r = Math.floor(Math.random() * 20) + 1;
    return { table: "INFESTATION", die: "d20", roll: r, effect: INFESTATION_TABLE[r - 1].effect };
  }
  const m = rollWeaponMishap();
  return { table: "WEAPON MISHAP", die: "d6", roll: m.roll, effect: m.effect };
}

// --- ATTACK RESOLUTION ---
// c = attacker, target = defender.
// weapon: { name, damage:"d6", type:"physical|nano|cyber" }
// attribute: 'strength'|'presence'|'agility'|'toughness'|'knowledge'
// dr: number (default 12)
// Returns a rich outcome describing every dice roll made.
export function resolveAttack({ attacker, target, weapon, attribute, dr = 12, mode = "strict", useAutofire = true, advantage = false }) {
  const attrMod = attacker.stats?.[attribute] || 0;
  // Advantage: roll d20 twice, keep higher
  const n1 = d20();
  const n2 = advantage ? d20() : n1;
  const nat = advantage ? Math.max(n1, n2) : n1;
  const total = nat + attrMod;

  const log = {
    attacker: attacker.name,
    target: target.name,
    weapon: weapon.name,
    weaponDamage: weapon.damage,
    attribute,
    dr,
    attack: { nat, mod: attrMod, total, advantage, rolls: advantage ? [n1, n2] : [n1] },
    outcome: null,       // 'hit' | 'crit' | 'miss' | 'fumble' | 'dodged'
    damageRoll: null,    // { rolls, total, max, notes }
    damageAfterArmor: 0,
    armorRoll: null,
    dodgeRoll: null,     // { nat, mod, total, pass }
    fumble: null,        // { table, die, roll, effect }
    hp_before: target.hp_current,
    hp_after: target.hp_current,
    broken: false,
  };

  // --- FUMBLE ---
  if (nat === 1) {
    log.outcome = "fumble";
    log.fumble = rollFumbleFor(weapon.type || "physical");
    return log;
  }

  // --- CRIT ---
  if (nat === 20) {
    log.outcome = "crit";
    const spec = parseDie(weapon.damage);
    const rolled = rollDie(spec);
    const critTotal = rolled.max + rolled.total;
    log.damageRoll = { ...rolled, total: critTotal, notes: `CRIT — MAX(${rolled.max}) + rolled(${rolled.total})` };
    // Crits ignore armor (book: full damage) and ignore dodge
    log.damageAfterArmor = critTotal;
    log.hp_after = Math.max(0, target.hp_current - critTotal);
    log.broken = log.hp_after === 0;
    return log;
  }

  // --- normal attack ---
  if (total < dr) {
    log.outcome = "miss";
    return log;
  }

  // Attack hits base DR — but defender rolls dodge (d20 + AGI vs DR)
  const dodgeNat = d20();
  const agiMod = target.stats?.agility || 0;
  const dodgeTotal = dodgeNat + agiMod;
  log.dodgeRoll = { nat: dodgeNat, mod: agiMod, total: dodgeTotal, pass: dodgeTotal >= dr };

  if (log.dodgeRoll.pass && mode === "strict") {
    log.outcome = "dodged";
    return log;
  }
  // HOUSE mode: successful dodge halves damage rather than negates
  const dodgeHalves = log.dodgeRoll.pass && mode === "house";

  // Roll damage
  const spec = parseDie(weapon.damage);
  let dmgRoll = rollDie(spec);
  if (spec?.autofire && useAutofire) {
    // Autofire: roll twice, keep the higher
    const r2 = rollDie(spec);
    if (r2.total > dmgRoll.total) dmgRoll = { ...r2, notes: `AUTOFIRE — kept higher of two rolls` };
    else dmgRoll = { ...dmgRoll, notes: `AUTOFIRE — kept higher of two rolls` };
  } else if (spec?.autofire && !useAutofire) {
    dmgRoll = { ...dmgRoll, notes: `SINGLE-FIRE — autofire suppressed` };
  }
  log.damageRoll = dmgRoll;

  // Subtract armor die (target.armor is a die-size int, e.g. 4 => d4)
  const armorSize = target.armor || 0;
  let armorRoll = 0;
  if (armorSize > 0) {
    armorRoll = Math.floor(Math.random() * armorSize) + 1;
    log.armorRoll = { die: armorSize, roll: armorRoll };
  }

  // Armor rule: a hit that lands still chips 1 HP even if armor fully absorbs it.
  let dmg = dmgRoll.total - armorRoll;
  if (dmg <= 0) dmg = 1;
  if (dodgeHalves) dmg = Math.max(1, Math.floor(dmg / 2));

  log.damageAfterArmor = dmg;
  log.outcome = "hit";
  log.hp_after = Math.max(0, target.hp_current - dmg);
  log.broken = log.hp_after === 0;
  return log;
}

// --- INITIATIVE ---
// Standard CY_BORG-style init: d20 + Agility. Higher goes first.
export function rollInitiative(c) {
  const nat = d20();
  const mod = c.stats?.agility || 0;
  return { nat, mod, total: nat + mod };
}

// Detect whether a foe has a stealth/ambush trait — they get to go first
// regardless of initiative roll (represents surprise round).
const STEALTH_RE = /stealth|cloak|silent|invisib|ambush|hidden|matte-black|surprise|first attack.*adv|shadow|phase/i;
export function isStealthy(c) {
  if (!c || c.side !== "foe") return false;
  const specials = c.specials || [];
  const type = String(c.type || "").toLowerCase();
  if (/skulker|whitetail|doppel|phase|assassin/.test(type)) return true;
  return specials.some((s) => STEALTH_RE.test(String(s)));
}

// --- MORALE CHECK ---
// After a foe drops / big hit — each remaining foe rolls d20 + Presence vs morale.
// Fail → flees (out of combat).
export function rollMorale(foe, dr = 12) {
  const nat = d20();
  const mod = foe.stats?.presence || 0;
  const total = nat + mod;
  // Compare against foe.morale (higher morale = braver): pass if total >= (dr adjusted by (12 - morale))
  const effectiveDR = dr + (12 - (foe.morale || 12));
  return { nat, mod, total, pass: total >= effectiveDR, dr: effectiveDR };
}

// --- FLATTEN a player character into a combatant ---
// Punks come from the sheet. Their armor is a die (e.g. StyleGuard Tier I = d2).
// Includes weapons + apps + nano powers, so any can be used as attack in the arena.
export function punkToCombatant(char) {
  // Armor: read the largest die size across equipped armor items.
  // CY_BORG armor tiers → damage-reduction die: I=d2, II=d4, III=d6, IV=d8.
  // Fallback: if tier is missing/unrecognised, parse the note text for a `-d?` value.
  const tierToSize = (t) => {
    const s = String(t || "").toUpperCase().trim();
    if (s === "I" || s === "1") return 2;
    if (s === "II" || s === "2") return 4;
    if (s === "III" || s === "3") return 6;
    if (s === "IV" || s === "4") return 8;
    return 0;
  };
  const parseNote = (text) => {
    const m = String(text || "").match(/[-−–]\s*d(\d+)/i);
    return m ? parseInt(m[1], 10) : 0;
  };
  const armor = (char.armor || []).reduce((best, a) => {
    if (!a) return best;
    const bySelfTier = tierToSize(a.tier);
    const byNote = parseNote(a.note || a.text || a.name || "");
    const size = Math.max(bySelfTier, byNote);
    return Math.max(best, size);
  }, 0);
  // Optional: stack subdermal armor plating (cybertech) — +2 die-size (adds a d2)
  // Only stack if we already have some armor die; otherwise use the d2 alone.
  const hasSubdermal = (char.cybertech || []).some((c) => {
    const t = String(c?.name || c?.text || "").toLowerCase();
    return /subdermal armor|subdermal armour/.test(t);
  });
  const finalArmor = hasSubdermal ? (armor > 0 ? Math.min(armor + 2, 8) : 2) : armor;

  const physicalWeapons = (char.weapons || []).map((w) => {
    const text = w.text || w.name || "";
    const dmg = w.damage || (text.match(/\(([^)]+)\)/) || [])[1] || "d4";
    const lower = text.toLowerCase();
    let type = "physical";
    if (/nano/.test(lower)) type = "nano";
    else if (/hack|deck|app/.test(lower)) type = "cyber";
    return { name: text.replace(/\s*\([^)]*\)\s*/, "").trim() || "Fist", damage: dmg, type };
  });
  // APPS as "cyber" attacks — only apps loaded into an installed cyberdeck count
  const deckIds = new Set((char.cybertech || [])
    .filter((it) => it && it.id && (it.is_deck || /cyberdeck/i.test(it.text || "")))
    .map((it) => it.id));
  const appWeapons = (char.apps || []).filter((a) => a.loaded_to && deckIds.has(a.loaded_to)).map((a) => {
    const text = a.text || a.name || "APP";
    const dmg = (text.match(/(\d*d\d+a?[+-]?\d*)/i) || [])[1] || "d4";
    const name = (text.split(" — ")[0] || text).trim();
    return { name: `[APP] ${name}`, damage: dmg, type: "cyber", app: true };
  });
  // NANO POWERS as "nano" attacks — extract damage die from text if present
  const nanoWeapons = (char.nano_powers || []).map((n) => {
    const text = n.text || n.name || "NANO";
    const dmg = (text.match(/(\d*d\d+a?[+-]?\d*)/i) || [])[1] || "d4";
    const name = (text.split(" — ")[0] || text).trim();
    return { name: `[NANO] ${name}`, damage: dmg, type: "nano" };
  });
  if (physicalWeapons.length === 0 && nanoWeapons.length === 0) {
    physicalWeapons.push({ name: "Unarmed", damage: "d2", type: "physical" });
  }
  const weapons = [...physicalWeapons, ...nanoWeapons, ...appWeapons];
  // Detect healing/advantage gear items
  const usableGear = (char.gear || []).map((g, i) => {
    const text = (g.text || g.name || "").toLowerCase();
    const raw = g.text || g.name || "";
    let effect = null;
    if (/first[- ]?aid|heal|medkit/.test(text)) {
      const dmg = (raw.match(/(\d*d\d+[+-]?\d*)/i) || [])[1] || "d4";
      effect = { kind: "heal", die: dmg, label: "HEAL" };
    } else if (/stimpack|stim|combat stim|amphet/.test(text)) {
      effect = { kind: "advantage", label: "ADV NEXT ATTACK" };
    } else if (/adrenal|reroll/.test(text)) {
      effect = { kind: "reroll", label: "REROLL LAST ROLL" };
    }
    return effect ? { id: g.id || `g${i}`, name: raw, effect } : null;
  }).filter(Boolean);
  return {
    id: char.id,
    side: "punk",
    name: char.name || "UNNAMED PUNK",
    stats: {
      presence: char.presence || 0,
      strength: char.strength || 0,
      agility: char.agility || 0,
      toughness: char.toughness || 0,
      knowledge: char.knowledge || 0,
    },
    hp_current: char.hp_current || char.hp_max || 1,
    hp_max: char.hp_max || 1,
    armor: finalArmor,
    morale: 12,
    weapons,
    usableGear,
    glitches_current: char.glitches_count || 0,
    glitches_max: char.glitches_count || 0,
    specials: [],
    initiative: 0,
    canJackIn: appWeapons.length > 0,
    jackedIn: false,
    _source: "punk",
  };
}

// --- FLATTEN a rolled foe into a combatant ---
export function foeToCombatant(foe) {
  const id = `foe_${Math.random().toString(36).slice(2, 10)}`;
  const weapons = (foe.weapons || [foe.weapon]).filter(Boolean).map((w) => {
    const lower = (w.name || "").toLowerCase();
    let type = "physical";
    if (/nano|blast|psychic|blessing|priest/.test(lower)) type = "nano";
    else if (/hack|deck|drone|micro-missile|jolt/.test(lower)) type = "cyber";
    return { name: w.name, damage: w.damage, type };
  });
  return {
    id,
    side: "foe",
    name: foe.name,
    type: foe.type,
    desc: foe.desc,
    stats: { ...foe.stats },
    hp_current: foe.hp,
    hp_max: foe.hp_max,
    armor: foe.armor || 0,
    morale: foe.morale || 8,
    weapons: weapons.length ? weapons : [{ name: "Claws", damage: "d4", type: "physical" }],
    specials: foe.specials || (foe.special ? [foe.special] : []),
    initiative: 0,
    _source: "foe",
  };
}
